/**
 * remark plugin for the two marks the dashboard's Tiptap editor
 * (`@baluu/markdown-editor`, serialised by `@tiptap/markdown`) writes with
 * non-standard syntax:
 *
 *   `++text++`  → underline  → `<u>`
 *   `==text==`  → highlight  → `<mark>`
 *
 * Neither CommonMark nor GFM knows these, so without this plugin every
 * customer-facing renderer shows the literal `++` / `==` the author never
 * typed. The plugin only splits `text` nodes, so code spans and fenced blocks
 * are untouched, and it emits mdast nodes carrying `data.hName`, which
 * remark-rehype turns into the element directly. Nothing here parses HTML, so
 * tenant-authored content still can't inject markup (no `rehype-raw`).
 *
 * Use it alongside `remark-gfm`, which covers the rest of what the editor
 * emits: `~~strikethrough~~`, tables, `- [x]` task lists and autolinks.
 */

interface TextNode {
  type: "text";
  value: string;
}

interface MarkNode {
  type: "editorMark";
  data: { hName: "u" | "mark" };
  children: PhrasingNode[];
}

type PhrasingNode = TextNode | MarkNode | { type: string; children?: unknown[] };

interface ParentNode {
  type: string;
  children: PhrasingNode[];
}

const MARK_TAGS: Record<string, "u" | "mark"> = {
  "++": "u",
  "==": "mark",
};

// Tokenises a text run into literal chunks and `++` / `==` delimiters.
// Written without lookbehind on purpose: this runs in the customer widget,
// and a lookbehind would throw at module load on Safari before 16.4 and take
// the whole page down with it.
const DELIMITER_PATTERN = /\+\+|==/g;

type Token =
  | { kind: "text"; value: string }
  | { kind: "delimiter"; value: string }
  | { kind: "node"; node: PhrasingNode };

function tokenize(children: PhrasingNode[]): Token[] {
  const tokens: Token[] = [];
  for (const child of children) {
    if (child.type !== "text") {
      tokens.push({ kind: "node", node: child });
      continue;
    }
    const value = (child as TextNode).value;
    let last = 0;
    for (const match of value.matchAll(DELIMITER_PATTERN)) {
      const start = match.index ?? 0;
      if (start > last) tokens.push({ kind: "text", value: value.slice(last, start) });
      tokens.push({ kind: "delimiter", value: match[0] });
      last = start + match[0].length;
    }
    if (last < value.length) tokens.push({ kind: "text", value: value.slice(last) });
  }
  return tokens;
}

// A delimiter can open a mark when what follows it isn't whitespace, and
// close one when what precedes it isn't whitespace — so `a == b == c` stays
// prose while `++under++` and `++**bold** text++` become marks. A node
// neighbour (bold, link, …) counts as non-whitespace content.
function canOpen(tokens: Token[], index: number): boolean {
  const next = tokens[index + 1];
  if (!next) return false;
  if (next.kind === "text") return !/^\s/.test(next.value);
  return true; // a node or another delimiter: non-whitespace either way
}

function canClose(tokens: Token[], index: number): boolean {
  const prev = tokens[index - 1];
  if (!prev) return false;
  if (prev.kind === "text") return !/\s$/.test(prev.value);
  return true;
}

function toNode(token: Token): PhrasingNode {
  if (token.kind === "node") return token.node;
  return { type: "text", value: token.value };
}

// Merges adjacent text nodes back together so the tree stays as remark
// would have produced it (one text node per run).
function pushNode(out: PhrasingNode[], node: PhrasingNode): void {
  const prev = out[out.length - 1];
  if (node.type === "text" && prev?.type === "text") {
    (prev as TextNode).value += (node as TextNode).value;
    return;
  }
  out.push(node);
}

function pairMarks(children: PhrasingNode[]): PhrasingNode[] {
  const tokens = tokenize(children);
  if (!tokens.some((t) => t.kind === "delimiter")) return children;

  // `out` holds finished nodes; `open` records where each unmatched opener
  // sits in `out` so a matching closer can wrap everything after it.
  const out: PhrasingNode[] = [];
  const open: Array<{ delimiter: string; index: number }> = [];

  tokens.forEach((token, i) => {
    if (token.kind !== "delimiter") {
      pushNode(out, toNode(token));
      return;
    }
    let opener: { delimiter: string; index: number } | undefined;
    for (let j = open.length - 1; j >= 0; j--) {
      if (open[j]!.delimiter === token.value) {
        opener = open[j];
        break;
      }
    }
    if (opener && canClose(tokens, i) && opener.index < out.length) {
      // Any other opener left open inside this span was never closed — it
      // stays literal text, which `pushNode` already recorded.
      open.splice(open.indexOf(opener));
      const inner = out.splice(opener.index);
      // Drop the opener's literal placeholder (always the first node).
      const [placeholder, ...content] = inner;
      const lead = (placeholder as TextNode).value.slice(token.value.length);
      const markChildren: PhrasingNode[] = [];
      if (lead) pushNode(markChildren, { type: "text", value: lead });
      for (const node of content) pushNode(markChildren, node);
      if (markChildren.length === 0) {
        // `++++`: nothing to wrap, so both delimiters stay literal.
        out.push(...inner, { type: "text", value: token.value });
        return;
      }
      out.push({ type: "editorMark", data: { hName: MARK_TAGS[token.value]! }, children: markChildren });
      return;
    }
    if (canOpen(tokens, i)) open.push({ delimiter: token.value, index: out.length });
    // Record the delimiter as literal text; if it turns out to open a mark
    // the closer strips it back off (see `lead` above). Pushed as its own
    // node so `index` points at it even when text precedes it.
    out.push({ type: "text", value: token.value });
  });

  // Re-merge text runs split by delimiters that ended up literal.
  const merged: PhrasingNode[] = [];
  for (const node of out) pushNode(merged, node);
  return merged;
}

function visit(node: PhrasingNode | ParentNode): void {
  if (!("children" in node) || !Array.isArray(node.children)) return;
  for (const child of node.children as PhrasingNode[]) visit(child as ParentNode);
  node.children = pairMarks(node.children as PhrasingNode[]);
}

/**
 * `remarkEditorMarks` — pass in `remarkPlugins` next to `remarkGfm`.
 */
export function remarkEditorMarks() {
  return (tree: ParentNode) => {
    visit(tree);
  };
}
